import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { getEnhancedAdminStats, EnhancedAdminStats } from '@/utils/enhanced-admin-helpers';
import { 
  Users, 
  UserCog, 
  Calendar, 
  Clock,
  AlertTriangle,
  CheckCircle,
  TrendingUp,
  DollarSign,
  Target,
  CreditCard,
  Wallet,
  PiggyBank,
  BarChart3
} from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { CustomBadge } from '@/components/ui/custom-badge';
import { format } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';

const AdminDashboard: React.FC = () => {
  const [stats, setStats] = useState<EnhancedAdminStats | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [financialOverview, setFinancialOverview] = useState<any>(null);
  const [financialLoading, setFinancialLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const dashboardStats = await getEnhancedAdminStats();
        setStats(dashboardStats);
      } catch (error) {
        console.error('Error fetching admin stats:', error);
      } finally {
        setIsLoading(false);
      }
    };

    const fetchFinancialOverview = async () => {
      try {
        const { data, error } = await supabase.functions.invoke('get-admin-financial-overview');
        if (error) {
          console.error('Error fetching financial overview:', error);
        } else {
          setFinancialOverview(data);
        }
      } catch (error) {
        console.error('Error fetching financial overview:', error);
      } finally {
        setFinancialLoading(false);
      }
    };

    fetchStats();
    fetchFinancialOverview();
  }, []);

  const formatDateTime = (dateString: string) => {
    try {
      return format(new Date(dateString), 'yyyy-MM-dd HH:mm');
    } catch (error) {
      return 'Invalid date';
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  const renderStatCard = (title: string, value: number | string, icon: React.ReactNode, description?: string, growth?: number) => (
    <Card className="h-full">
      <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
        <CardTitle className="text-xs sm:text-sm font-medium text-gray-500 truncate">{title}</CardTitle>
        <div className="flex-shrink-0">{icon}</div>
      </CardHeader>
      <CardContent className="space-y-1">
        <div className="text-xl sm:text-2xl font-bold">
          {isLoading ? <Skeleton className="h-6 sm:h-8 w-16 sm:w-20" /> : value}
        </div>
        {description && <p className="text-xs text-gray-500">{description}</p>}
        {growth !== undefined && !isLoading && (
          <p className="text-xs text-green-600 flex items-center">
            <TrendingUp className="h-3 w-3 mr-1" />
            +{growth} this month
          </p>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-4 sm:space-y-6">
      <div>
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-2">Admin Dashboard</h2>
        <p className="text-sm sm:text-base text-muted-foreground">
          Overview of platform performance and key metrics
        </p>
      </div>
      
      {/* Key Metrics */}
      <div className="grid gap-2 sm:gap-3 lg:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
        {renderStatCard(
          "Total Clients", 
          stats?.clientCount || 0, 
          <Users className="h-4 w-4 text-gray-500" />,
          undefined,
          stats?.monthlyGrowth.clients
        )}
        {renderStatCard(
          "Total Practitioners", 
          stats?.practitionerCount || 0, 
          <UserCog className="h-4 w-4 text-gray-500" />,
          undefined,
          stats?.monthlyGrowth.practitioners
        )}
        {renderStatCard(
          "Pending", 
          stats?.pendingPractitionerCount || 0, 
          <AlertTriangle className="h-4 w-4 text-amber-500" />
        )}
        {renderStatCard(
          "Total Bookings", 
          stats?.bookingCount || 0, 
          <Calendar className="h-4 w-4 text-gray-500" />,
          undefined,
          stats?.monthlyGrowth.bookings
        )}
      </div>

      {/* Financial Overview */}
      <div className="grid gap-2 sm:gap-3 lg:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
        {renderStatCard(
          "Total Platform Revenue", 
          formatCurrency(financialOverview?.totalPlatformRevenue || 0), 
          <DollarSign className="h-4 w-4 text-green-500" />,
          "All-time platform revenue"
        )}
        {renderStatCard(
          "Monthly Revenue", 
          formatCurrency(financialOverview?.monthlyRevenue || 0), 
          <TrendingUp className="h-4 w-4 text-blue-500" />,
          "This month's revenue"
        )}
        {renderStatCard(
          "Platform Commission", 
          formatCurrency(financialOverview?.totalPlatformCommission || 0), 
          <CreditCard className="h-4 w-4 text-purple-500" />,
          `${financialOverview?.platformCommissionRate || 0}% commission`
        )}
        {renderStatCard(
          "Practitioner Earnings", 
          formatCurrency(financialOverview?.totalPractitionerEarnings || 0), 
          <Wallet className="h-4 w-4 text-amber-500" />,
          "Total paid to practitioners"
        )}
      </div>

      {/* Performance Metrics */}
      <div className="grid gap-2 sm:gap-3 lg:gap-4 grid-cols-1 sm:grid-cols-3">
        {renderStatCard(
          "Conversion Rate", 
          `${stats?.conversionRate || 0}%`, 
          <Target className="h-4 w-4 text-blue-500" />,
          "Clients who made bookings"
        )}
        {renderStatCard(
          "Avg Booking Value", 
          formatCurrency(financialOverview?.averageBookingValue || stats?.avgBookingValue || 0), 
          <DollarSign className="h-4 w-4 text-green-500" />,
          "Average revenue per booking"
        )}
        {renderStatCard(
          "Weekly Growth", 
          `+${stats?.weeklyGrowth.clients || 0}`, 
          <TrendingUp className="h-4 w-4 text-purple-500" />,
          "New clients this week"
        )}
      </div>

      {/* Top Performing Practitioners */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base sm:text-lg">Top Performing Practitioners</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            {isLoading ? (
              <div className="space-y-2">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs sm:text-sm whitespace-nowrap">Name</TableHead>
                    <TableHead className="text-xs sm:text-sm whitespace-nowrap">Bookings</TableHead>
                    <TableHead className="text-xs sm:text-sm whitespace-nowrap">Revenue</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stats?.topPerformingPractitioners?.length ? (
                    stats.topPerformingPractitioners.map((practitioner) => (
                      <TableRow key={practitioner.id}>
                        <TableCell className="font-medium text-xs sm:text-sm">{practitioner.name}</TableCell>
                        <TableCell className="text-xs sm:text-sm">{practitioner.totalBookings}</TableCell>
                        <TableCell className="text-xs sm:text-sm">{formatCurrency(practitioner.totalRevenue)}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center text-xs sm:text-sm">No data available</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-2 sm:gap-3 lg:gap-4 grid-cols-1 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-3 sm:pb-6">
            <CardTitle className="text-sm sm:text-base lg:text-lg">Recently Joined Clients</CardTitle>
          </CardHeader>
          <CardContent className="p-3 sm:p-6">
            <div className="overflow-x-auto">
              {isLoading ? (
                <div className="space-y-2">
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : (
                <Table>
                   <TableHeader>
                     <TableRow>
                       <TableHead className="text-xs sm:text-sm whitespace-nowrap">Name</TableHead>
                       <TableHead className="text-xs sm:text-sm whitespace-nowrap">Email</TableHead>
                       <TableHead className="text-xs sm:text-sm whitespace-nowrap">Registration Date</TableHead>
                     </TableRow>
                   </TableHeader>
                  <TableBody>
                    {stats?.recentClients?.length ? (
                      stats.recentClients.map((client: any) => (
                        <TableRow key={client.id}>
                          <TableCell className="font-medium text-xs sm:text-sm">
                            {`${client.first_name || ''} ${client.last_name || ''}`.trim() || 'Not Set'}
                          </TableCell>
                          <TableCell className="text-xs sm:text-sm">{client.email || 'Not Set'}</TableCell>
                          <TableCell className="text-xs sm:text-sm">{formatDateTime(client.created_at)}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={3} className="text-center text-xs sm:text-sm">No data available</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base sm:text-lg">Recently Joined Practitioners</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              {isLoading ? (
                <div className="space-y-2">
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs sm:text-sm whitespace-nowrap">Name</TableHead>
                      <TableHead className="text-xs sm:text-sm whitespace-nowrap">Status</TableHead>
                      <TableHead className="text-xs sm:text-sm whitespace-nowrap">Registration Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stats?.recentPractitioners?.length ? (
                      stats.recentPractitioners.map((practitioner: any) => (
                        <TableRow key={practitioner.id}>
                          <TableCell className="font-medium text-xs sm:text-sm">
                            {`${practitioner.first_name || ''} ${practitioner.last_name || ''}`.trim() || 'Not Set'}
                          </TableCell>
                          <TableCell className="text-xs sm:text-sm">
                            <CustomBadge variant={
                              practitioner.profile_status === 'pending_review' ? "outline" :
                              practitioner.profile_status === 'approved' ? "success" : "destructive"
                            }>
                              {
                                practitioner.profile_status === 'pending_review' ? 'Pending Review' :
                                practitioner.profile_status === 'approved' ? 'Approved' : 
                                practitioner.profile_status === 'rejected' ? 'Rejected' : practitioner.profile_status
                              }
                            </CustomBadge>
                          </TableCell>
                          <TableCell className="text-xs sm:text-sm">{formatDateTime(practitioner.created_at)}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={3} className="text-center text-xs sm:text-sm">No data available</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base sm:text-lg">Recent Bookings</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            {isLoading ? (
              <div className="space-y-2">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs sm:text-sm whitespace-nowrap">Client</TableHead>
                    <TableHead className="text-xs sm:text-sm whitespace-nowrap">Practitioner</TableHead>
                    <TableHead className="text-xs sm:text-sm whitespace-nowrap">Start Time</TableHead>
                    <TableHead className="text-xs sm:text-sm whitespace-nowrap">Status</TableHead>
                    <TableHead className="text-xs sm:text-sm whitespace-nowrap">Price</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stats?.recentBookings?.length ? (
                    stats.recentBookings.map((booking: any) => (
                      <TableRow key={booking.id}>
                        <TableCell className="font-medium text-xs sm:text-sm">
                          {`${booking.clients?.first_name || ''} ${booking.clients?.last_name || ''}`.trim() || 'Unknown'}
                        </TableCell>
                        <TableCell className="text-xs sm:text-sm">
                          {`${booking.practitioners?.first_name || ''} ${booking.practitioners?.last_name || ''}`.trim() || 'Unknown'}
                        </TableCell>
                        <TableCell className="text-xs sm:text-sm">
                          <div className="flex items-center">
                            <Clock className="mr-1 h-3 w-3" />
                            {formatDateTime(booking.start_time)}
                          </div>
                        </TableCell>
                        <TableCell className="text-xs sm:text-sm">
                          <CustomBadge variant={
                            booking.status === 'Scheduled' ? "outline" :
                            booking.status === 'Completed' ? "success" :
                            booking.status === 'Cancelled' ? "destructive" : "secondary"
                          }>
                            {booking.status}
                          </CustomBadge>
                        </TableCell>
                        <TableCell className="text-xs sm:text-sm">{formatCurrency(parseFloat(booking.price_at_booking || 0))} {booking.currency_at_booking}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-xs sm:text-sm">No booking data available</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminDashboard;
